import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';

import { GuestStatus } from 'types/guest.types';
import { Separator } from '@epoch-ui/components/Separator';
import { Button } from '@epoch-ui/components/Button';
import EditIcon from 'components/common/icons/EditIcon';
import { EventType } from 'types/event.types';
import { QuestionResponse } from 'types/question-response.types';

import { useGuestSideSheetContext } from '../guestSideSheetContext';
import { EditQuestionnaireModal } from '../Modals';
import { formatResponse } from '../utils';

const registrationStatuses = [
  GuestStatus.CONFIRMED,
  GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY,
  GuestStatus.WAITLISTED
];

const ResponseSection: React.FC = () => {
  const {
    guestData: {
      guest: { surveyResponses = [], surveySubmittedAt },
      event: { preEventQuestions }
    }
  } = useGuestSideSheetContext();
  const mostRecentSubmissionDate: Date | null = useMemo(() => {
    if (!surveySubmittedAt) return null;
    const submissionDate = new Date(surveySubmittedAt);
    return !Number.isNaN(submissionDate.getTime()) ? submissionDate : null;
  }, [surveySubmittedAt]);

  const responseMap: Record<number, QuestionResponse> = surveyResponses.reduce(
    (acc, response) => ({
      ...acc,
      [response.id]: response
    }),
    {}
  );
  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-4">
        {preEventQuestions.map((question) => {
          const surveyResponse = question.id ? responseMap[question.id] : null;
          const response = surveyResponse ? formatResponse(surveyResponse) : 'N/A';
          return (
            <div key={question.id}>
              <div className="tw-mb-1 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                {question.label}
              </div>
              <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
                {response || '-'}
              </div>
            </div>
          );
        })}
      </div>
      {!!mostRecentSubmissionDate && (
        <div className="tw-mb-1 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
          Questionnaire last updated: {format(mostRecentSubmissionDate, 'PPp')}
        </div>
      )}
    </>
  );
};

export const GuestSideSheetQuestionnaireSection = (): React.ReactNode => {
  const {
    eventOrSessionId,
    guestData: { guest, event, sessionStatuses },
    refetchGuestData
  } = useGuestSideSheetContext();
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] = useState(false);
  const hasQuestionnaire = event.preeventEnable && event.preEventQuestions.length > 0;

  const isGuestRegistered: boolean = useMemo(() => {
    if (event.eventType === EventType.MULTI_SESSIONS) {
      return (
        sessionStatuses.length > 0 &&
        sessionStatuses.some(({ status }) => registrationStatuses.includes(status))
      );
    } else {
      return registrationStatuses.includes(guest.status);
    }
  }, [event, guest, sessionStatuses]);

  const canModify: boolean = useMemo(() => {
    if (event.eventType === EventType.MULTI_SESSIONS) {
      const sessions = event.sessions || [];
      const pastSessionIds = sessions.filter(({ hasEnded }) => hasEnded).map(({ id }) => id);
      if (event.limitRegistration) {
        return !sessionStatuses.some(
          ({ id, status }) => pastSessionIds.includes(id) && registrationStatuses.includes(status)
        );
      } else {
        return !pastSessionIds.includes(eventOrSessionId);
      }
    } else {
      return !event.hasEnded;
    }
  }, [event, sessionStatuses, eventOrSessionId]);

  if (!hasQuestionnaire) return null;
  if (!isGuestRegistered) {
    return (
      <>
        <Separator />
        <div className="tw-mb-2 tw-flex tw-flex-row tw-justify-between">
          <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
            This guest hasn&#39;t registered, so they aren&#39;t required to fill out the
            questionnaire.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Separator />
      <div className="tw-flex tw-flex-row tw-justify-between">
        <span className="tw-flex tw-items-center tw-text-base tw-font-medium">
          Event questionnaire
        </span>
        {canModify && (
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-[8px]"
            onClick={(): void => {
              setIsQuestionnaireModalOpen(true);
            }}
          >
            <div className="tw-mr-[2px] tw-flex tw-items-center">
              <EditIcon />
            </div>
            Edit
          </Button>
        )}
      </div>
      <ResponseSection />
      <EditQuestionnaireModal
        open={isQuestionnaireModalOpen}
        onOpenChange={() => setIsQuestionnaireModalOpen(false)}
        onSubmit={refetchGuestData}
      />
    </>
  );
};
